import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  api,
  setUnauthorizedHandler,
  setBillingSuspendedHandler,
  setAccessToken,
  tryRefresh,
  hydrateTokensFromStore,
  clearPersistedTokens,
} from "../api/client.js";
import { saveTokens } from "../lib/tokenStore.js";
import { isNative } from "../lib/platform.js";
import { unregisterCurrentDevice } from "../lib/pushMobile.js";
import {
  isMaster as roleIsMaster,
  isOwner as roleIsOwner,
  isMember as roleIsMember,
} from "../lib/roles.js";
import { syncPushSubscription } from "../lib/pushClient.js";
import { clearResourceCache } from "../lib/resourceCache.js";

const AuthContext = createContext(null);

/**
 * Normalize a user payload from the backend (camelCase or snake_case) into the
 * canonical shape stored in context. Keeps `tenant_id` ready for multi-tenant
 * features even before the backend exposes it for every user.
 */
function normalizeUser(u) {
  if (!u) return null;
  return {
    id: u.id,
    email: u.email,
    role: u.role,
    fullName: u.fullName,
    mfaEnabled: u.mfaEnabled ?? false,
    publicSlug: u.publicSlug ?? u.public_slug ?? null,
    tenant_id: u.tenant_id ?? u.tenantId ?? null,
    createdAt: u.createdAt ?? u.created_at ?? null,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // { id, email, role, fullName }
  const [tenant, setTenant] = useState(null); // tenant object from /api/me/context
  const [plan, setPlan] = useState(null); // plan object from /api/me/context
  const [loading, setLoading] = useState(true); // true while checking session on mount
  const [billingSuspended, setBillingSuspended] = useState(null); // BILLING_SUSPENDED body or null

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* best-effort */
    }
    setAccessToken(null);
    // En nativo, des-registrar el token push de ESTE equipo del backend ANTES
    // de borrar el almacén, para que un dispositivo con sesión cerrada deje de
    // recibir las alertas CRITICAL de este usuario. Best-effort: un fallo de
    // red no puede romper el logout.
    if (isNative()) await unregisterCurrentDevice().catch(() => {});
    // Y borrar los tokens persistidos, para que la sesión cerrada no sobreviva
    // a un reinicio de la app.
    if (isNative()) await clearPersistedTokens().catch(() => {});
    setUser(null);
    setTenant(null);
    setPlan(null);
    // Las listas cacheadas de /admin son datos de ESTE usuario. Sin este
    // borrado, quien entrara después en la misma pestaña vería la flota del
    // anterior pintada al instante mientras se revalida.
    clearResourceCache();
  }, []);

  // On mount: check if there's an active session.
  //
  // Boot sequence for iOS Safari ITP / incognito users (cross-site cookies
  // blocked): call tryRefresh() first so _accessToken is populated before
  // getMe() fires.  On non-ITP browsers the refresh response just refreshes
  // the cookies silently; the Bearer token is an added bonus.  If refresh
  // fails (incognito / no refresh cookie) we still attempt getMe() — it may
  // succeed via the httpOnly cookie on non-ITP browsers or will fail cleanly
  // → login page.  This fixes the reload regression tracked in issue #92.
  useEffect(() => {
    (async () => {
      // En nativo, restaurar la sesión persistida (access/refresh) al cliente en
      // memoria ANTES de la secuencia refresh/getMe, para que el usuario que
      // vuelve entre autenticado en frío. En web es no-op (lo cierra isNative).
      if (isNative()) await hydrateTokensFromStore().catch(() => {});

      // Proactively refresh — populates _accessToken from the response body so
      // subsequent requests can send it as a Bearer header even when cookies are
      // blocked by ITP.  Ignore the return value: getMe() decides the outcome.
      await tryRefresh().catch(() => {});

      const loadContext = () =>
        api.getMeContext().then(({ user: u, tenant: t, plan: p }) => {
          setUser(normalizeUser(u));
          setTenant(t ?? null);
          setPlan(p ?? null);
        });

      try {
        await loadContext();
      } catch {
        // DEV-ONLY: auto-login al master si scripts/dev-up.sh lo habilitó.
        // Gate doble: import.meta.env.DEV (Vite build mode) + VITE_DEV_AUTO_LOGIN
        // explícito. En prod build ambos son falsy → no se intenta.
        const devAutoLogin =
          import.meta.env.DEV &&
          import.meta.env.VITE_DEV_AUTO_LOGIN === "true";
        if (devAutoLogin) {
          try {
            const data = await api.devLogin();
            if (data?.accessToken) setAccessToken(data.accessToken);
            await loadContext();
          } catch {
            setUser(null);
            setTenant(null);
            setPlan(null);
          }
        } else {
          setUser(null);
          setTenant(null);
          setPlan(null);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const userId = user?.id;

  // Cambio de identidad → la caché de listas de /admin se tira.
  //
  // Limpiarla solo en `logout` dejaba un hueco real: `/login` es una ruta
  // pública que NO rebota a quien ya tiene sesión, así que en una máquina
  // compartida un segundo usuario puede entrar sin que el primero cierre, y
  // `directLogin`/`verifyMfa` solo cambian `user`. La caché sobrevivía a ese
  // cambio y la siguiente pantalla de Flota pintaba —sin pedir nada— la flota
  // del usuario anterior: placas, conductores con teléfono, IMEIs e ICCIDs.
  // El aislamiento por owner_id que impone RLS en el backend no sirve de nada
  // si al backend no se le pregunta.
  //
  // Colgado del id y no de cada camino de login: así cubre también el que se
  // invente mañana. Solo dispara en id→OTRO id; el logout ya limpia por su
  // cuenta y el arranque (undefined→id) no tiene nada que tirar.
  const previousUserIdRef = useRef(null);
  useEffect(() => {
    const previous = previousUserIdRef.current;
    previousUserIdRef.current = userId;
    if (previous && userId && previous !== userId) clearResourceCache();
  }, [userId]);

  // Re-publicar la suscripción push del navegador en cuanto hay sesión.
  // Ver syncPushSubscription: cuando el navegador rota el endpoint, el backend
  // borra la fila y el usuario deja de recibir alertas para siempre sin ninguna
  // señal en la UI. Esta es la única pasada que lo repara sola. Corre también
  // después de un login, no solo en el arranque, porque el push va por usuario.
  //
  // Además queda escuchando a `push:resubscribe`, que el service worker emite
  // cuando el navegador rotó el endpoint: sin eso, con la pestaña abierta el
  // canal seguiría roto hasta la próxima recarga completa.
  useEffect(() => {
    if (!userId) return undefined;
    syncPushSubscription(userId).catch(() => {});

    if (typeof navigator === "undefined" || !navigator.serviceWorker) return undefined;
    const onSwMessage = (event) => {
      if (event.data?.type !== "push:resubscribe") return;
      syncPushSubscription(userId).catch(() => {});
    };
    navigator.serviceWorker.addEventListener("message", onSwMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onSwMessage);
  }, [userId]);

  // Register the unauthorized handler so 401s anywhere in the app trigger logout.
  // Also register the billing-suspended handler so 402 BILLING_SUSPENDED responses
  // surface the overlay without requiring components to handle errors manually.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAccessToken(null);
      setUser(null);
      clearResourceCache();
    });
    setBillingSuspendedHandler((body) => {
      setBillingSuspended(body);
    });
  }, []);

  const login = useCallback((email, password) => {
    return api.login({ email, password });
  }, []);

  const verifyMfa = useCallback(async (tempToken, code) => {
    const data = await api.verifyMfa({ tempToken, code });
    if (data.accessToken) setAccessToken(data.accessToken);
    // En nativo, persistir los dos tokens (el cuerpo trae refreshToken).
    if (isNative())
      saveTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      }).catch(() => {});
    const normalized = normalizeUser({
      ...data.user,
      mfaEnabled: data.user.mfaEnabled ?? true,
    });
    setUser(normalized);
    // Fetch full context so tenant/plan are populated after MFA login
    api
      .getMeContext()
      .then(({ tenant: t, plan: p }) => {
        setTenant(t ?? null);
        setPlan(p ?? null);
      })
      .catch((err) => {
        console.error("Failed to load me/context post-login:", err);
      });
    return normalized;
  }, []);

  // Used when login succeeds without MFA step (mfa_enabled = false).
  // `refreshToken` solo llega en nativo (viene en el cuerpo); la web pasa 2 args.
  const directLogin = useCallback((userData, accessToken = null, refreshToken = null) => {
    if (accessToken) setAccessToken(accessToken);
    // En nativo, persistir los dos para que la sesión sobreviva al reinicio.
    if (isNative()) saveTokens({ accessToken, refreshToken }).catch(() => {});
    setUser(normalizeUser(userData));
    // Fetch tenant/plan context so they are populated immediately after login,
    // without requiring a page reload (same pattern as verifyMfa).
    api
      .getMeContext()
      .then(({ tenant: t, plan: p }) => {
        setTenant(t ?? null);
        setPlan(p ?? null);
      })
      .catch((err) => {
        console.error("Failed to load me/context post-login:", err);
      });
  }, []);

  // Update local user state (e.g. after editing profile name)
  const updateUser = useCallback((data) => {
    setUser((prev) => (prev ? { ...prev, ...data } : prev));
  }, []);

  const clearBillingSuspended = useCallback(() => {
    setBillingSuspended(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      tenant,
      plan,
      loading,
      login,
      verifyMfa,
      directLogin,
      logout,
      updateUser,
      // Billing enforcement state — set when API returns 402 BILLING_SUSPENDED
      billingSuspended,
      clearBillingSuspended,
      // Convenience role flags so components can do `const { isMaster } = useAuth()`.
      isMaster: roleIsMaster(user),
      isOwner: roleIsOwner(user),
      isMember: roleIsMember(user),
    }),
    [user, tenant, plan, loading, login, verifyMfa, directLogin, logout, updateUser, billingSuspended, clearBillingSuspended],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
